import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  Autocomplete
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Plus as PlusIcon, RefreshCw } from 'lucide-react';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { toast } from 'react-toastify';
import api from '../../services/api';

const CitiesTab = () => {
  const [cities, setCities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [currentCity, setCurrentCity] = useState({ name: '', postal_code: '', province: '', region_id: '' });
  const [errors, setErrors] = useState({});
  
  // Pagination & Search
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countries, setCountries] = useState([]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    fetchCities();
  }, [page, rowsPerPage, debouncedSearch, selectedCountry]);

  useEffect(() => {
    if (open) fetchRegions();
  }, [open]);

  const fetchCountries = async () => {
    try {
      const response = await api.get('/countries?all=true');
      setCountries(response.data.data || response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await api.get('/regions?all=true');
      setRegions(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCities = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page, 
        limit: rowsPerPage,
        search: debouncedSearch,
        country_id: selectedCountry || ''
      });
      
      const response = await api.get(`/cities?${queryParams}`);
      
      setCities(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error(error);
      toast.error('Errore nel caricamento delle città');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (city = null) => {
    if (city) {
      setEditMode(true);
      setCurrentCity(city);
    } else {
      setEditMode(false);
      setCurrentCity({ name: '', postal_code: '', province: '', region_id: '' });
    }
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentCity({ name: '', postal_code: '', province: '', region_id: '' });
  };

  const validate = () => {
    let tempErrors = {};
    if (!currentCity.name) tempErrors.name = 'Il nome è obbligatorio';
    if (!currentCity.province) tempErrors.province = 'La provincia è obbligatoria';
    if (!currentCity.region_id) tempErrors.region_id = 'La regione è obbligatoria';
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      if (editMode) {
        await api.put(`/cities/${currentCity.id}`, currentCity);
      } else {
        await api.post('/cities', currentCity);
      }

      toast.success(editMode ? 'Città aggiornata' : 'Città creata');
      fetchCities();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.msg || error.message || 'Errore nel salvataggio');
    }
  };

  const openDeleteDialog = (city) => {
    setDeleteTarget(city);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/cities/${deleteTarget.id}`);
      toast.success('Città eliminata');
      closeDeleteDialog();
      fetchCities();
    } catch (error) {
      console.error(error);
      toast.error('Impossibile eliminare: probabilmente ci sono dati collegati');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Cerca città..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        
        <Autocomplete
          id="country-filter"
          options={countries}
          getOptionLabel={(option) => option.name}
          value={countries.find(c => c.id === selectedCountry) || null}
          onChange={(event, newValue) => {
            setSelectedCountry(newValue ? newValue.id : '');
            setPage(0);
          }}
          sx={{ width: 250 }}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Filtra per Nazione" 
              size="small"
              placeholder="Seleziona nazione..."
            />
          )}
        />

        <Box sx={{ flexGrow: 1 }} />

        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => handleOpen()}
        >
          Nuova Città
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Provincia</TableCell>
              <TableCell>CAP</TableCell>
              <TableCell>Regione</TableCell>
              <TableCell>Nazione</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" className="py-8">
                  <RefreshCw className="animate-spin h-6 w-6 text-gray-400 mx-auto" />
                </TableCell>
              </TableRow>
            ) : cities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Nessuna città trovata</TableCell>
              </TableRow>
            ) : (
              cities.map((city) => (
                <TableRow key={city.id}>
                  <TableCell>{city.name}</TableCell>
                  <TableCell>{city.province}</TableCell>
                  <TableCell>{city.postal_code}</TableCell>
                  <TableCell>{city.region}</TableCell>
                  <TableCell>{city.country}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifica">
                      <IconButton size="small" onClick={() => handleOpen(city)} color="primary">
                        <EditIcon size={18} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina">
                      <IconButton size="small" onClick={() => openDeleteDialog(city)} color="error">
                        <DeleteIcon size={18} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Righe per pagina"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
      />

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editMode ? 'Modifica Città' : 'Nuova Città'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome Città"
            type="text"
            fullWidth
            value={currentCity.name}
            onChange={(e) => setCurrentCity({ ...currentCity, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Provincia"
            type="text"
            fullWidth
            value={currentCity.province}
            onChange={(e) => setCurrentCity({ ...currentCity, province: e.target.value })}
            error={!!errors.province}
            helperText={errors.province}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="CAP"
            type="text"
            fullWidth
            value={currentCity.postal_code}
            onChange={(e) => setCurrentCity({ ...currentCity, postal_code: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth error={!!errors.region_id}>
            <InputLabel>Regione</InputLabel>
            <Select
              value={currentCity.region_id}
              label="Regione"
              onChange={(e) => setCurrentCity({ ...currentCity, region_id: e.target.value })}
            >
              {regions.map((region) => (
                <MenuItem key={region.id} value={region.id}>
                  {region.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annulla</Button>
          <Button onClick={handleSubmit} variant="contained">Salva</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Questa azione è irreversibile. Stai per eliminare la città:
          </DialogContentText>
          <Box sx={{ mt: 1, fontWeight: 700 }}>
            {deleteTarget?.name || '-'}
          </Box>
          <Box sx={{ color: 'text.secondary', fontSize: 14 }}>
            Provincia: {deleteTarget?.province || '-'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>Annulla</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CitiesTab;
